import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight h-11";

export function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/submissions", { category: "contact", ...form });
      setDone(true);
      toast.success("Message sent!", { description: data.message });
    } catch (err) {
      toast.error("Could not send message", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="text-center py-12">
        <CheckCircle2 className="mx-auto text-blaze-orange" size={54} />
        <h3 className="mt-4 font-heading text-3xl tracking-wide text-white">Message Sent!</h3>
        <p className="mt-2 text-white/70">Thanks for reaching out — we'll get back to you shortly.</p>
        <Button
          data-testid="contact-reset-btn"
          onClick={() => {
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
            setDone(false);
          }}
          className="mt-6 bg-blaze-blue hover:bg-blaze-blueLight text-white font-bold uppercase tracking-wide rounded-full px-8"
        >
          Send Another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Name *</Label>
          <Input required data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Email *</Label>
          <Input required type="email" data-testid="contact-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Phone</Label>
          <Input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(203) 555-0123" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Subject</Label>
          <Input data-testid="contact-subject" className={inputCls} value={form.subject} onChange={set("subject")} placeholder="What's this about?" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-white/80 text-xs uppercase tracking-wider">Message *</Label>
        <Textarea required data-testid="contact-message" className="bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight min-h-[130px]" value={form.message} onChange={set("message")} placeholder="How can we help?" />
      </div>
      <Button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="w-full bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 text-base shadow-[0_8px_28px_rgba(246,140,31,0.35)] transition-all"
      >
        {loading ? <Loader2 className="animate-spin" /> : "Send Message"}
      </Button>
    </form>
  );
}
